import React, { useState, useEffect } from 'react';
import { Save, Loader, Check } from 'lucide-react';
import { db } from '../../../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import '../AdminPanel.css';

const SettingsSection = () => {
    const [settings, setSettings] = useState({
        siteTitle: 'BPO Consultants',
        siteDescription: 'Leading BPO Consulting Firm in the Philippines',
        contactEmail: 'info@bpoconsultants.ph',
        leadNotificationEmail: '', // Global email for leads
        enableRegistration: false,
        maintenanceMode: false
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const docRef = doc(db, 'settings', 'general');
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    setSettings(prev => ({ ...prev, ...docSnap.data() }));
                }
            } catch (error) {
                console.error("Error fetching settings:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchSettings();
    }, []);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setSettings(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        setSaved(false);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await setDoc(doc(db, 'settings', 'general'), settings);
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
        } catch (error) {
            console.error("Error saving settings:", error);
            alert("Failed to save settings.");
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="panel active"><div className="p-10 text-center text-muted">Loading settings...</div></div>;

    return (
        <div className="panel active">
            <div className="page-hdr">
                <div className="page-hdr-left">
                    <div className="eyebrow">Configuration</div>
                    <h1>Settings <span>.</span></h1>
                </div>
                <div className="page-hdr-actions">
                    <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                        {saving ? <Loader size={14} className="animate-spin" /> : saved ? <Check size={14} /> : <Save size={14} />}
                        {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Changes'}
                    </button>
                </div>
            </div>

            <div className="card" style={{ maxWidth: '600px' }}>
                <div className="card-hdr">
                    <div className="card-hdr-title">General Settings</div>
                </div>
                <div className="card-body">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

                        <div className="form-group">
                            <label style={{ display: 'block', fontSize: '11px', color: 'var(--muted)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '.1em' }}>Site Title</label>
                            <input
                                type="text"
                                name="siteTitle"
                                value={settings.siteTitle}
                                onChange={handleChange}
                                className="form-input"
                                style={{ width: '100%', padding: '10px' }}
                            />
                        </div>

                        <div className="form-group">
                            <label style={{ display: 'block', fontSize: '11px', color: 'var(--muted)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '.1em' }}>Site Description</label>
                            <textarea
                                name="siteDescription"
                                value={settings.siteDescription}
                                onChange={handleChange}
                                className="form-textarea"
                                style={{ width: '100%', padding: '10px', minHeight: '80px', resize: 'vertical' }}
                            />
                        </div>

                        <div className="form-group">
                            <label style={{ display: 'block', fontSize: '11px', color: 'var(--muted)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '.1em' }}>Contact Email (Displayed on Site)</label>
                            <input
                                type="email"
                                name="contactEmail"
                                value={settings.contactEmail}
                                onChange={handleChange}
                                className="form-input"
                                style={{ width: '100%', padding: '10px' }}
                            />
                        </div>

                        <div className="form-group">
                            <label style={{ display: 'block', fontSize: '11px', color: 'var(--accent)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '.1em', fontWeight: 'bold' }}>Global Lead Notification Email</label>
                            <div className="text-xs text-[var(--muted)] mb-2">Where should new lead notifications be sent?</div>
                            <input
                                type="email"
                                name="leadNotificationEmail"
                                value={settings.leadNotificationEmail}
                                onChange={handleChange}
                                placeholder="e.g. sales@bpoconsultants.ph"
                                className="form-input"
                                style={{ width: '100%', padding: '10px', borderColor: 'var(--accent)' }}
                            />
                        </div>

                        <div style={{ height: '1px', background: 'var(--border)', margin: '10px 0' }}></div>

                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div>
                                <div style={{ color: 'var(--text)', fontSize: '13px', fontWeight: 500 }}>Maintenance Mode</div>
                                <div style={{ color: 'var(--muted)', fontSize: '11px' }}>Disable public access to the site</div>
                            </div>
                            <label className="switch" style={{ position: 'relative', display: 'inline-block', width: '34px', height: '20px' }}>
                                <input
                                    type="checkbox"
                                    name="maintenanceMode"
                                    checked={settings.maintenanceMode}
                                    onChange={handleChange}
                                    style={{ opacity: 0, width: 0, height: 0 }}
                                />
                                <span style={{
                                    position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0,
                                    backgroundColor: settings.maintenanceMode ? 'var(--accent)' : 'var(--surface3)',
                                    transition: '.4s', borderRadius: '34px'
                                }}>
                                    <span style={{
                                        position: 'absolute', content: '', height: '14px', width: '14px', left: '3px', bottom: '3px',
                                        backgroundColor: 'white', transition: '.4s', borderRadius: '50%',
                                        transform: settings.maintenanceMode ? 'translateX(14px)' : 'translateX(0)'
                                    }}></span>
                                </span>
                            </label>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
};

export default SettingsSection;
